import { BaseClient } from '../base-client';
import type {
  ActiveForUserQuery,
  CreateTimesheetUnlockBody,
  ListTimesheetUnlocksQuery,
  RevokeTimesheetUnlockBody,
  TimesheetUnlock,
  UpdateTimesheetUnlockBody,
} from './types';

export class TimesheetUnlocksClient extends BaseClient {
  /** GET /api/timesheet-unlocks → server-paginated/sorted/filtered list of
   *  unlocks for the tenant. Returns the standard `{ data, meta }` envelope.
   *  `status` defaults to `active` server-side. */
  async list(
    query: ListTimesheetUnlocksQuery = {},
  ): Promise<{ data: TimesheetUnlock[]; meta: { total: number; limit: number; offset: number } }> {
    return this.request('/api/timesheet-unlocks', {
      query: {
        status: query.status,
        sortField: query.sortField,
        sortDirection: query.sortDirection,
        limit: query.limit,
        offset: query.offset,
      },
    });
  }

  /** GET /api/timesheet-unlocks/active → active unlocks covering one
   *  employee's week (timesheet.view). Drives the grid "unlocked" hint. */
  async listActiveForUser(query: ActiveForUserQuery): Promise<{ data: TimesheetUnlock[] }> {
    return this.request('/api/timesheet-unlocks/active', {
      query: { userId: query.userId, fromDate: query.fromDate, toDate: query.toDate },
    });
  }

  /** GET /api/timesheet-unlocks/:id → a single unlock (seeds the edit sheet). */
  async get(id: string): Promise<{ data: TimesheetUnlock }> {
    return this.request(`/api/timesheet-unlocks/${id}`);
  }

  /** POST /api/timesheet-unlocks → create an unlock. `related.overlaps`
   *  carries any active unlocks the new one overlaps (warn-but-allow). */
  async create(
    body: CreateTimesheetUnlockBody,
  ): Promise<{ data: TimesheetUnlock; related: { overlaps: TimesheetUnlock[] } }> {
    return this.request('/api/timesheet-unlocks', { method: 'POST', body });
  }

  /** PUT /api/timesheet-unlocks/:id → full edit (scope + range + auto-end +
   *  reason). Same `{ data, related: { overlaps } }` shape as create. */
  async update(
    id: string,
    body: UpdateTimesheetUnlockBody,
  ): Promise<{ data: TimesheetUnlock; related: { overlaps: TimesheetUnlock[] } }> {
    return this.request(`/api/timesheet-unlocks/${id}`, { method: 'PUT', body });
  }

  /** POST /api/timesheet-unlocks/:id/revoke → end an unlock early. */
  async revoke(
    id: string,
    body: RevokeTimesheetUnlockBody = {},
  ): Promise<{ data: TimesheetUnlock }> {
    return this.request(`/api/timesheet-unlocks/${id}/revoke`, { method: 'POST', body });
  }
}
